/**
 * NSE Options Trade Page
 * Layout: Left panel (Top Gainers/Losers) + Right panel (10 Option Chain Frames)
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchOrderBook, fetchTradeBook, cancelOrder } from '../redux/slices/orderSlice';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy } from '@dnd-kit/sortable';
// import TopMoversPanel from '../components/NseOptions/TopMoversPanel'; // Commented out - replaced by Nifty50Panel
import Nifty50Panel from '../components/NseOptions/Nifty50Panel';
import OptionChainFrame from '../components/NseOptions/OptionChainFrame';
import SymbolPositionSelector from '../components/NseOptions/SymbolPositionSelector';
import aliceBlueWS from '../services/aliceBlueWebSocket';
import api from '../services/api';
import useKeyboardShortcuts from '../hooks/useKeyboardShortcuts';
import ShortcutInfoDrawer from '../components/Shortcuts/ShortcutInfoDrawer';

const NseOptionsTrade = () => {
  const dispatch = useDispatch();
  const { userSession, userId } = useSelector((state) => state.auth);
  const { tradeBook, orderBook } = useSelector((state) => state.orders);
  const [positions, setPositions] = useState({});
  const [lockedPositions, setLockedPositions] = useState(new Set());
  const [availableSymbols, setAvailableSymbols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // Nifty 50 panel collapse state
  const [isNiftyPanelCollapsed, setIsNiftyPanelCollapsed] = useState(() => {
    return localStorage.getItem('nseOptions_niftyPanelCollapsed') === 'true';
  });

  // Dynamic grid: 4 columns × N rows (1-15)
  const [gridRows, setGridRows] = useState(() => {
    const saved = localStorage.getItem('nseOptions_gridRows');
    return saved ? parseInt(saved) : 1;
  });
  const totalPositions = gridRows * 4;
  const positionIds = useMemo(() => Array.from({ length: totalPositions }, (_, i) => i + 1), [totalPositions]);
  
  // WebSocket state
  const [livePrices, setLivePrices] = useState({});
  const [wsConnected, setWsConnected] = useState(false);
  const [wsStatus, setWsStatus] = useState('Initializing...');

  // Symbol selection from Top Movers
  const [selectedSymbolForPosition, setSelectedSymbolForPosition] = useState(null);
  const [showPositionModal, setShowPositionModal] = useState(false);

  // Master user / Group trading state
  const [isMasterUser, setIsMasterUser] = useState(false);
  const [userGroups, setUserGroups] = useState([]);
  const [pendingOrderData, setPendingOrderData] = useState(null);
  const [showGroupSelectModal, setShowGroupSelectModal] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState(() => {
    return localStorage.getItem('nseOptions_selectedGroup') || null;
  });
  const [orderMessage, setOrderMessage] = useState({ show: false, text: '', type: '' });

  // Flip view: option chains vs positions/trades/orders
  const [isGridFlipped, setIsGridFlipped] = useState(() => {
    return localStorage.getItem('nseOptions_gridFlipped') === 'true';
  });
  const [positionsViewMode, setPositionsViewMode] = useState('positions');
  const [portfolioPositions, setPortfolioPositions] = useState([]);
  const [rawPositions, setRawPositions] = useState([]);
  const [hoveredPosition, setHoveredPosition] = useState(null);
  const [hoveredOrder, setHoveredOrder] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, dir: 'desc' });

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // Initialize WebSocket
  useEffect(() => {
    let mounted = true;

    const handlePriceUpdate = (priceData) => {
      if (!mounted) return;
      // WebSocket provides exchange in priceData.e (or priceData.exchange) and token in priceData.tk (or priceData.token)
      // Key format should match WebSocket: exchange|token
      const exchange = priceData.exchange || priceData.e || 'NSE';
      const token = priceData.token || priceData.tk;
      const key = `${exchange}|${token}`;
      
      setLivePrices(prev => {
        // Merge new data with existing data to preserve previous values
        // This prevents "long back LTP" issue when websocket data is incomplete
        return {
          ...prev,
          [key]: {
            ...prev[key],     // Keep all previous values
            ...priceData,     // Update with new values
            // Ensure LTP is never set to 0 or null if we have a previous value
            ltp: (priceData.lp && priceData.lp > 0) ? priceData.lp : (priceData.ltp && priceData.ltp > 0) ? priceData.ltp : (prev[key]?.ltp || priceData.lp || priceData.ltp),
            // Ensure percentage change is preserved if not in new data
            pc: (priceData.pc !== undefined && priceData.pc !== null) ? priceData.pc : (prev[key]?.pc),
            // Preserve high and low values
            h: (priceData.h !== undefined && priceData.h !== null) ? priceData.h : prev[key]?.h,
            l: (priceData.l !== undefined && priceData.l !== null) ? priceData.l : prev[key]?.l,
            c: (priceData.c !== undefined && priceData.c !== null) ? priceData.c : prev[key]?.c,
            o: (priceData.o !== undefined && priceData.o !== null) ? priceData.o : prev[key]?.o
          }
        };
      });
    };

    const initWebSocket = async () => {
      try {
        setWsStatus('Connecting...');
        await aliceBlueWS.connect();
        
        if (!mounted) return;
        
        setWsConnected(true);
        setWsStatus('Connected');
        
        // Add subscriber for price updates (wildcard for all symbols)
        aliceBlueWS.addSubscriber('*', handlePriceUpdate);

        console.log('✅ [NSE Options Trade] WebSocket connected - subscriber added');

      } catch (error) {
        console.error('❌ [NSE Options Trade] WebSocket connection failed:', error);
        if (mounted) {
          setWsStatus(`Error: ${error.message}`);
          setWsConnected(false);
        }
      }
    };

    initWebSocket();

    return () => {
      mounted = false;
      aliceBlueWS.removeSubscriber('*', handlePriceUpdate);
    };
  }, []); // Empty dependency array - only run once

  // Debug: Log when livePrices state updates (throttled)
  useEffect(() => {
    const priceCount = Object.keys(livePrices).length;
    if (priceCount > 0 && priceCount % 10 === 0) {
      console.log(`📊 [NSE Options Trade] livePrices tracking ${priceCount} instruments`);
    }
  }, [livePrices]);

  // Helper function to clean symbol names
  const cleanSymbolName = (symbol) => {
    if (!symbol) return symbol;
    // Remove exchange suffixes like -EQ, -BE, etc.
    return symbol.replace(/-(EQ|BE|BZ|SM|ST|SL|IL|IT)$/i, '').trim();
  };

  // Load positions from backend on mount
  useEffect(() => {
    const loadPositions = async () => {
      try {
        const response = await api.get('/user-preferences/nse-options-positions', {
          headers: { Authorization: `Bearer ${userSession}` }
        });
        
        if (response.data.success && response.data.data) {
          const { positions: savedPositions, locks: savedLocks } = response.data.data;
          
          if (savedPositions && typeof savedPositions === 'object') {
            // Clean all symbol names
            const cleaned = {};
            Object.keys(savedPositions).forEach(key => {
              cleaned[key] = cleanSymbolName(savedPositions[key]);
            });
            setPositions(cleaned);
            console.log('✅ Loaded positions from backend:', cleaned);
          }
          
          if (Array.isArray(savedLocks)) {
            setLockedPositions(new Set(savedLocks));
            console.log('✅ Loaded locks from backend:', savedLocks);
          }
        }
      } catch (error) {
        console.error('Failed to load positions from backend:', error);
        // Fallback to localStorage if backend fails
        const localPositions = localStorage.getItem('nseOptionsPositions');
        const localLocks = localStorage.getItem('nseOptionsLocks');
        
        if (localPositions) {
          try {
            const parsed = JSON.parse(localPositions);
            const cleaned = {};
            Object.keys(parsed).forEach(key => {
              cleaned[key] = cleanSymbolName(parsed[key]);
            });
            setPositions(cleaned);
            console.log('✅ Loaded positions from localStorage:', cleaned);
          } catch (e) {
            console.error('Failed to parse local positions:', e);
          }
        }
        
        if (localLocks) {
          try {
            setLockedPositions(new Set(JSON.parse(localLocks)));
          } catch (e) {
            console.error('Failed to parse local locks:', e);
          }
        }
      } finally {
        // Mark initial load as complete
        setInitialLoadComplete(true);
      }
    };

    if (userSession) {
      loadPositions();
    }
  }, [userSession]);

  // Fetch available symbols
  useEffect(() => {
    const fetchSymbols = async () => {
      try {
        const response = await api.get('/nse-options-trade/symbols', {
          headers: { Authorization: `Bearer ${userSession}` }
        });
        if (response.data.success) {
          setAvailableSymbols(response.data.symbols);
        }
      } catch (error) {
        console.error('Failed to fetch symbols:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSymbols();
  }, [userSession]);

  // Check if user is master and load groups
  useEffect(() => {
    const checkMasterStatus = async () => {
      try {
        const response = await api.get('/groups/check-master');
        if (response.data.success) {
          setIsMasterUser(response.data.isMaster);
          if (response.data.isMaster) {
            const groupsResponse = await api.get('/groups');
            if (groupsResponse.data.success) {
              setUserGroups(groupsResponse.data.groups);
            }
          }
        }
      } catch (error) {
        console.error('Error checking master status:', error);
      }
    };
    if (userId) {
      checkMasterStatus();
    }
  }, [userId]);

  // Fetch portfolio positions when grid is flipped to positions view
  useEffect(() => {
    if (!isGridFlipped) return;
    
    const fetchPositionsData = async () => {
      try {
        const response = await api.get('/portfolio/positions');
        if (response.data.success && response.data.data) {
          // Filter only NFO/BFO Options (exclude Futures, NSE, BSE, MCX)
          const filtered = response.data.data.filter(pos => {
            if (pos.exchange !== 'NFO' && pos.exchange !== 'BFO') return false;
            const symbol = pos.tradingsymbol || pos.tradingSymbol || '';
            const isFuture = symbol.includes('FUT') || /\d{2}[A-Z]{3}\d{2}F$/i.test(symbol);
            const isOption = /CE$/i.test(symbol) || /PE$/i.test(symbol);
            return !isFuture && isOption;
          });
          setRawPositions(filtered);
        }
      } catch (error) {
        console.error('Failed to fetch positions:', error);
      }
    };

    fetchPositionsData();
    const interval = setInterval(fetchPositionsData, 10000);
    return () => clearInterval(interval);
  }, [isGridFlipped]);

  // Transform raw positions with live prices
  useEffect(() => {
    if (rawPositions.length === 0) {
      setPortfolioPositions([]);
      return;
    }
    
    const transformed = rawPositions.map(pos => {
      const instrumentId = pos.token || pos.instrumentId || pos.instrument_token;
      const key = `${pos.exchange}|${instrumentId}`;
      const priceData = livePrices[key];
      
      let ltp = 0;
      if (priceData?.ltp) ltp = priceData.ltp;
      else if (priceData?.lp) ltp = priceData.lp;
      else if (pos.ltp) ltp = parseFloat(pos.ltp);
      
      const buyQty = parseFloat(pos.buyQuantity || pos.buyQty || pos.buyqty || pos.dayBuyQuantity || 0);
      const sellQty = parseFloat(pos.sellQuantity || pos.sellQty || pos.sellqty || pos.daySellQuantity || 0);
      const netQty = parseInt(pos.netQuantity || pos.netQty || pos.netqty || (buyQty - sellQty));
      
      let buyAvg = 0;
      if (pos.buyPrice && parseFloat(pos.buyPrice) > 0) buyAvg = parseFloat(pos.buyPrice);
      else if (pos.mtmBuyPrice && parseFloat(pos.mtmBuyPrice) > 0) buyAvg = parseFloat(pos.mtmBuyPrice);
      else if (buyQty > 0 && pos.netAveragePrice && netQty > 0) buyAvg = parseFloat(pos.netAveragePrice);
      
      let sellAvg = 0;
      if (pos.sellPrice && parseFloat(pos.sellPrice) > 0) sellAvg = parseFloat(pos.sellPrice);
      else if (pos.mtmSellprice && parseFloat(pos.mtmSellprice) > 0) sellAvg = parseFloat(pos.mtmSellprice);
      else if (sellQty > 0 && pos.netAveragePrice && netQty < 0) sellAvg = parseFloat(pos.netAveragePrice);
      
      const closedQty = Math.min(buyQty, sellQty);
      const realizedPnl = (sellAvg - buyAvg) * closedQty;
      let unrealizedPnl = 0;
      if (buyQty > sellQty) unrealizedPnl = (ltp - buyAvg) * (buyQty - sellQty);
      else if (sellQty > buyQty) unrealizedPnl = (sellAvg - ltp) * (sellQty - buyQty);
      const dayPnl = realizedPnl + unrealizedPnl;
      
      const productType = pos.product || pos.prd || 'NRML';
      let optionType = '';
      const sym = pos.tradingSymbol || pos.tradingsymbol || '';
      if (sym.includes('CE')) optionType = 'CE';
      else if (sym.includes('PE')) optionType = 'PE';
      
      return {
        type: productType,
        instrument: pos.displayName || pos.tradingSymbol || pos.tradingsymbol,
        optionType,
        qty: netQty,
        buyAvg: buyAvg ? buyAvg.toFixed(2) : '0.00',
        sellAvg: sellAvg ? sellAvg.toFixed(2) : '0.00',
        ltp: ltp > 0 ? ltp.toFixed(2) : (pos.ltp || '0.00'),
        dayPnl: dayPnl.toFixed(2),
        mtm: (netQty !== 0 ? (ltp - (netQty > 0 ? buyAvg : sellAvg)) * Math.abs(netQty) : 0).toFixed(0),
        transaction: netQty > 0 ? 'Buy' : 'Sell',
        rawData: pos
      };
    });
    setPortfolioPositions(transformed);
  }, [rawPositions, livePrices]);

  // Fetch trades/orders when switching tabs in flipped view
  useEffect(() => {
    if (!isGridFlipped) return;
    if (positionsViewMode === 'trades') {
      dispatch(fetchTradeBook());
    } else if (positionsViewMode === 'orderbook') {
      dispatch(fetchOrderBook());
    }
  }, [positionsViewMode, isGridFlipped, dispatch]);

  const calculateTotalMTM = useCallback(() => {
    if (portfolioPositions.length === 0) return '0.00';
    return portfolioPositions.reduce((sum, pos) => sum + parseFloat(pos.dayPnl || 0), 0).toFixed(2);
  }, [portfolioPositions]);

  const handleExitPosition = useCallback(async (pos) => {
    if (!pos.rawData) return;
    const netQty = parseInt(pos.qty);
    if (netQty === 0) return;
    
    const transType = netQty > 0 ? 'SELL' : 'BUY';
    const qty = Math.abs(netQty);
    const tradingSymbol = pos.rawData.tradingSymbol || pos.rawData.tradingsymbol;
    
    if (!window.confirm(`Exit ${tradingSymbol}? ${transType} ${qty} qty at market price`)) return;
    
    try {
      const orderPayload = {
        exchange: pos.rawData.exchange,
        tradingSymbol,
        instrumentId: pos.rawData.token || pos.rawData.instrumentId,
        transactionType: transType,
        quantity: qty,
        price: '0',
        orderType: 'MARKET',
        validity: 'DAY',
        product: pos.type || 'NRML',
        orderComplexity: 'SIMPLE',
        deviceId: 'WEB',
        userId
      };
      const response = await api.post('/orders/place', orderPayload);
      if (response.data.success) {
        setOrderMessage({ show: true, text: `Exit order placed: ${transType} ${qty} ${tradingSymbol}`, type: 'success' });
      } else {
        setOrderMessage({ show: true, text: 'Exit failed: ' + (response.data.error || 'Unknown'), type: 'error' });
      }
    } catch (error) {
      setOrderMessage({ show: true, text: 'Exit failed: ' + (error.response?.data?.error || error.message), type: 'error' });
    }
    setTimeout(() => setOrderMessage({ show: false, text: '', type: '' }), 3000);
  }, [userId]);

  const handleCancelOrderFromBook = useCallback(async (order) => {
    if (!window.confirm(`Cancel order: ${order.tradingSymbol}?`)) return;
    try {
      await dispatch(cancelOrder(order.brokerOrderId)).unwrap();
      setOrderMessage({ show: true, text: `Order cancelled: ${order.tradingSymbol}`, type: 'success' });
      dispatch(fetchOrderBook());
    } catch (error) {
      setOrderMessage({ show: true, text: 'Cancel failed: ' + (error.message || 'Unknown'), type: 'error' });
    }
    setTimeout(() => setOrderMessage({ show: false, text: '', type: '' }), 3000);
  }, [dispatch]);

  // Parse trading symbol to extract symbol name, expiry, strike, option type
  const parseSymbol = useCallback((tradingSymbol) => {
    if (!tradingSymbol) return { name: '-', strike: '', optType: '', expiry: '', display: '-' };
    // Pattern: NIFTY26FEB25000CE or BANKNIFTY26FEB45000PE or RELIANCE26FEB1400CE
    const match = tradingSymbol.match(/^([A-Z]+)(\d{2}[A-Z]{3})(\d+)(CE|PE)$/i);
    if (match) {
      return {
        name: match[1],
        expiry: match[2],
        strike: match[3],
        optType: match[4].toUpperCase(),
        display: `${match[1]} ${match[3]} ${match[4].toUpperCase()}`
      };
    }
    // Try alternate pattern: NIFTY24D25000CE (single letter month)
    const match2 = tradingSymbol.match(/^([A-Z]+)(\d{2}[A-Z])(\d+)(CE|PE)$/i);
    if (match2) {
      return {
        name: match2[1],
        expiry: match2[2],
        strike: match2[3],
        optType: match2[4].toUpperCase(),
        display: `${match2[1]} ${match2[3]} ${match2[4].toUpperCase()}`
      };
    }
    return { name: tradingSymbol, strike: '', optType: '', expiry: '', display: tradingSymbol };
  }, []);

  // Save positions to backend with debouncing
  useEffect(() => {
    // Skip if initial load not complete (prevents saving empty state on mount)
    if (!initialLoadComplete) {
      return;
    }
    
    // Skip if no userSession
    if (!userSession) {
      return;
    }

    // Debounce save to backend (2 seconds after last change)
    const saveTimer = setTimeout(async () => {
      try {
        await api.post('/user-preferences/nse-options-positions', {
          positions,
          locks: [...lockedPositions]
        }, {
          headers: { Authorization: `Bearer ${userSession}` }
        });
        
        // Also save to localStorage as backup
        localStorage.setItem('nseOptionsPositions', JSON.stringify(positions));
        localStorage.setItem('nseOptionsLocks', JSON.stringify([...lockedPositions]));
        
        console.log('✅ Positions saved to backend and localStorage', { positions, locks: [...lockedPositions] });
      } catch (error) {
        console.error('❌ Failed to save positions to backend:', error);
        // Still save to localStorage if backend fails
        localStorage.setItem('nseOptionsPositions', JSON.stringify(positions));
        localStorage.setItem('nseOptionsLocks', JSON.stringify([...lockedPositions]));
      }
    }, 2000); // 2 second debounce

    return () => clearTimeout(saveTimer);
  }, [positions, lockedPositions, userSession, initialLoadComplete]);

  // Handle adding symbol to position
  const handleAddSymbol = useCallback((symbol, position) => {
    // Clean symbol name before adding
    const cleanedSymbol = cleanSymbolName(symbol);
    setPositions(prev => ({
      ...prev,
      [position]: cleanedSymbol
    }));
  }, []);

  // Handle symbol selection from Top Movers
  const handleSymbolSelectFromMovers = useCallback((symbol) => {
    const cleanedSymbol = cleanSymbolName(symbol);
    console.log(`📌 Symbol selected from Top Movers: ${cleanedSymbol}`);
    setSelectedSymbolForPosition(cleanedSymbol);
    setShowPositionModal(true);
  }, []);

  // Handle position selection from modal
  const handlePositionSelect = useCallback((position) => {
    if (selectedSymbolForPosition && position) {
      handleAddSymbol(selectedSymbolForPosition, parseInt(position));
      setShowPositionModal(false);
      setSelectedSymbolForPosition(null);
    }
  }, [selectedSymbolForPosition, handleAddSymbol]);

  // Handle removing symbol from position
  const handleRemoveSymbol = useCallback((position) => {
    setPositions(prev => {
      const newPositions = { ...prev };
      delete newPositions[position];
      return newPositions;
    });
    // Also unlock if it was locked
    setLockedPositions(prev => {
      const newLocks = new Set(prev);
      newLocks.delete(position);
      return newLocks;
    });
  }, []);

  // Handle lock toggle
  const handleToggleLock = useCallback((position) => {
    setLockedPositions(prev => {
      const newLocks = new Set(prev);
      if (newLocks.has(position)) {
        newLocks.delete(position);
      } else {
        newLocks.add(position);
      }
      return newLocks;
    });
  }, []);

  // Handle drag end
  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const activePos = parseInt(active.id);
    const overPos = parseInt(over.id);

    // Check if either position is locked
    if (lockedPositions.has(activePos) || lockedPositions.has(overPos)) {
      alert('Cannot move locked positions');
      return;
    }

    // Swap positions
    setPositions(prev => {
      const newPositions = { ...prev };
      const temp = newPositions[activePos];
      newPositions[activePos] = newPositions[overPos];
      newPositions[overPos] = temp;
      
      // Clean up undefined values
      if (newPositions[activePos] === undefined) {
        delete newPositions[activePos];
      }
      if (newPositions[overPos] === undefined) {
        delete newPositions[overPos];
      }
      
      return newPositions;
    });
  }, [lockedPositions]);

  // Get available positions for dropdown - memoized
  const availablePositions = useMemo(() => {
    const available = [];
    for (let i = 1; i <= totalPositions; i++) {
      if (!positions[i] || !lockedPositions.has(i)) {
        available.push(i);
      }
    }
    return available;
  }, [positions, lockedPositions, totalPositions]);

  // ==================== KEYBOARD SHORTCUT HANDLERS ====================
  const shortcutActionHandlers = {
    // Positions / Views
    view_positions: () => { setIsGridFlipped(true); setPositionsViewMode('positions'); },
    view_trades: () => { setIsGridFlipped(true); setPositionsViewMode('trades'); },
    view_orderbook: () => { setIsGridFlipped(true); setPositionsViewMode('orderbook'); },
    exit_selected_position: () => { if (hoveredPosition) handleExitPosition(hoveredPosition); },
  };

  const { shortcutsEnabled, setShortcutsEnabled, shortcuts, lastTriggered } = useKeyboardShortcuts('nse-options-trade', shortcutActionHandlers);
  // ==================== END KEYBOARD SHORTCUT HANDLERS ====================

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-indigo-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-indigo-900 p-1">
      {/* Header */}
      <div className="flex items-center justify-between py-1 mb-1 px-2">
        <h1 className="text-xl font-bold text-white flex items-center gap-2">
          <span>📊</span>
          NSE Options Trade
        </h1>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShortcutsEnabled(!shortcutsEnabled)}
            className={`px-2 py-1 rounded text-xs font-medium border transition-all duration-200 ${
              shortcutsEnabled
                ? 'bg-purple-600 text-white border-purple-600 shadow-sm shadow-purple-200'
                : 'bg-gray-800 text-gray-400 border-gray-600 hover:border-purple-400 hover:text-purple-400'
            }`}
            title={shortcutsEnabled ? 'Keyboard shortcuts active - Click to disable' : 'Enable keyboard shortcuts'}
          >
            ⌨️ {shortcutsEnabled ? 'Shortcuts ON' : 'Shortcuts'}
          </button>
          <ShortcutInfoDrawer shortcuts={shortcuts} variant="dark" />
          {lastTriggered && (Date.now() - lastTriggered.timestamp < 2000) && (
            <div className="text-xs bg-purple-900 text-purple-200 px-2 py-0.5 rounded animate-pulse">
              ⌨️ {lastTriggered.key} → {lastTriggered.action}
            </div>
          )}
        </div>
      </div>

      <div className="flex gap-1 h-[calc(100vh-60px)]">
        {/* Left Panel: Nifty 50 Symbols with Live LTP & %Change */}
        {!isNiftyPanelCollapsed ? (
          <div className="w-[350px] flex-shrink-0 flex overflow-hidden">
            <div className="flex-1 min-w-0 overflow-hidden">
              <Nifty50Panel 
                livePrices={livePrices}
                wsConnected={wsConnected}
                onSymbolSelect={handleSymbolSelectFromMovers}
              />
            </div>
            <button
              onClick={() => {
                setIsNiftyPanelCollapsed(true);
                localStorage.setItem('nseOptions_niftyPanelCollapsed', 'true');
              }}
              className="flex-none bg-blue-900 hover:bg-blue-1000 text-white w-5 rounded-r-lg flex items-center justify-center transition-colors ml-[-1px]"
              title="Collapse Nifty 50 Panel"
            >
              ◀
            </button>
          </div>
        ) : (
          <button
            onClick={() => {
              setIsNiftyPanelCollapsed(false);
              localStorage.setItem('nseOptions_niftyPanelCollapsed', 'false');
            }}
            className="bg-blue-900 hover:bg-blue-1000 text-white w-8 rounded-lg shadow-lg flex items-center justify-center self-stretch transition-colors flex-shrink-0"
            title="Expand Nifty 50 Panel"
          >
            <span className="-rotate-90 whitespace-nowrap text-[13px] font-semibold">📊 Nifty 50 ▶</span>
          </button>
        )}

        {/* Right Panel: Option Chain Frames */}
        <div className="flex-1 flex flex-col gap-1 overflow-hidden">
          {/* Symbol Selector + Grid Size Selector */}
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-2 border border-white/20 relative z-[10000]">
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <SymbolPositionSelector
                  availableSymbols={availableSymbols}
                  availablePositions={availablePositions}
                  onAddSymbol={handleAddSymbol}
                  onSymbolSelect={handleSymbolSelectFromMovers}
                />
              </div>
              {/* P/T/O Flip Button - next to search */}
              <div className="flex-1">
              <button
                onClick={() => {
                  const newVal = !isGridFlipped;
                  setIsGridFlipped(newVal);
                  localStorage.setItem('nseOptions_gridFlipped', newVal.toString());
                  if (newVal) setPositionsViewMode('positions');
                }}
                className={`px-2.5 py-1 rounded text-xs font-bold transition-all border flex-shrink-0 ${
                  isGridFlipped
                    ? 'bg-purple-600 border-purple-400 text-white hover:bg-purple-700'
                    : 'bg-white/15 border-white/30 text-white/80 hover:bg-white/25 hover:text-white'
                }`}
                title={isGridFlipped ? 'Switch to Option Chains' : 'Switch to Positions / Trades / Orders'}
              >
                {isGridFlipped ? '📊 Chains' : '📋 P/T/O'}
              </button>
                </div>
              {/* Grid Size Selector */}
              <div className="flex items-center gap-1.5 flex-shrink-0">
                {!isGridFlipped && (
                  <>
                    <span className="text-white/70 text-xs font-semibold">Grid:</span>
                    <select
                      value={gridRows}
                      onChange={(e) => {
                        const val = parseInt(e.target.value);
                        setGridRows(val);
                        localStorage.setItem('nseOptions_gridRows', val.toString());
                      }}
                      className="bg-white/20 text-white border border-white/30 rounded px-2 py-1 text-xs font-bold focus:outline-none focus:ring-1 focus:ring-blue-400"
                    >
                      {Array.from({ length: 15 }, (_, i) => i + 1).map(r => (
                        <option key={r} value={r} className="bg-gray-800 text-white">
                          {r}({r * 4})
                        </option>
                      ))}
                    </select>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Option Chain Frames Grid - 4 per row (normal) OR Positions/Trades/Orders (flipped) */}
          {!isGridFlipped ? (
          <div className="flex-1 overflow-auto">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={positionIds}
                strategy={rectSortingStrategy}
              >
                <div className="grid grid-cols-2 xl:grid-cols-4 lg:grid-cols-4 md:grid-cols-3 gap-1 auto-rows-max">
                  {positionIds.map((position) => (
                    <OptionChainFrame
                      key={position}
                      position={position}
                      symbol={positions[position]}
                      isLocked={lockedPositions.has(position)}
                      onRemove={() => handleRemoveSymbol(position)}
                      onToggleLock={() => handleToggleLock(position)}
                      livePrices={livePrices}
                      wsConnected={wsConnected}
                      isMasterUser={isMasterUser}
                      userGroups={userGroups}
                      onGroupOrder={async (orderPayload) => {
                        const skipPopup = localStorage.getItem('trading_skipGroupPopup') === 'true';
                        if (skipPopup) {
                          const defaultMode = localStorage.getItem('trading_defaultMode') || 'myaccount';
                          const defaultGroupId = defaultMode.startsWith('group_') ? defaultMode.replace('group_', '') : null;
                          try {
                            let response;
                            if (defaultGroupId) {
                              response = await api.post('/orders/place-group', { groupId: parseInt(defaultGroupId), ...orderPayload });
                              if (response.data.success) {
                                let msg = `Group order! Success: ${response.data.successCount}, Failed: ${response.data.failCount}`;
                                if (response.data.needsReloginCount > 0) msg += ` (${response.data.needsReloginCount} need re-login)`;
                                setOrderMessage({ show: true, text: msg, type: 'success' });
                              } else {
                                setOrderMessage({ show: true, text: 'Group order failed: ' + (response.data.error || 'Unknown'), type: 'error' });
                              }
                            } else {
                              response = await api.post('/orders/place', orderPayload);
                              if (response.data.success) {
                                setOrderMessage({ show: true, text: 'Order placed successfully!', type: 'success' });
                              } else {
                                setOrderMessage({ show: true, text: 'Order failed: ' + (response.data.error || 'Unknown'), type: 'error' });
                              }
                            }
                          } catch (err) {
                            setOrderMessage({ show: true, text: 'Failed: ' + (err.response?.data?.error || err.message), type: 'error' });
                          }
                          setTimeout(() => setOrderMessage({ show: false, text: '', type: '' }), 4000);
                          return;
                        }
                        setPendingOrderData(orderPayload);
                        setShowGroupSelectModal(true);
                      }}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>
          ) : (
          /* Flipped View: Positions / Trades / Orders */
          <div className="flex-1 overflow-auto">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg border border-white/20 overflow-hidden">
              {/* Tab Header */}
              <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-4 py-1.5 font-semibold text-white flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <span className="text-sm">
                    {positionsViewMode === 'positions' ? 'Positions' : 
                     positionsViewMode === 'trades' ? 'Trade Book' : 'Order Book'}
                  </span>
                  {positionsViewMode === 'positions' && (
                    <>
                      <button onClick={() => setPositionsViewMode('trades')}
                        className="bg-white/20 hover:bg-white/30 px-2 py-0.5 rounded text-xs transition-all">🔄 Trades</button>
                      <button onClick={() => setPositionsViewMode('orderbook')}
                        className="bg-white/20 hover:bg-white/30 px-2 py-0.5 rounded text-xs transition-all">🔄 Orders</button>
                    </>
                  )}
                  {positionsViewMode === 'trades' && (
                    <>
                      <button onClick={() => setPositionsViewMode('positions')}
                        className="bg-white/20 hover:bg-white/30 px-2 py-0.5 rounded text-xs transition-all">🔄 Positions</button>
                      <button onClick={() => setPositionsViewMode('orderbook')}
                        className="bg-white/20 hover:bg-white/30 px-2 py-0.5 rounded text-xs transition-all">🔄 Orders</button>
                    </>
                  )}
                  {positionsViewMode === 'orderbook' && (
                    <>
                      <button onClick={() => setPositionsViewMode('positions')}
                        className="bg-white/20 hover:bg-white/30 px-2 py-0.5 rounded text-xs transition-all">🔄 Positions</button>
                      <button onClick={() => setPositionsViewMode('trades')}
                        className="bg-white/20 hover:bg-white/30 px-2 py-0.5 rounded text-xs transition-all">🔄 Trades</button>
                    </>
                  )}
                </div>
                <span className="bg-white/20 px-3 py-0.5 rounded-full text-xs">
                  <span className="text-blue-100 font-medium">{userId}</span>
                  {positionsViewMode === 'positions' && (
                    <span className={`ml-2 font-bold ${parseFloat(calculateTotalMTM()) >= 0 ? 'text-green-300' : 'text-red-300'}`}>
                      MTM: {calculateTotalMTM()}
                    </span>
                  )}
                </span>
              </div>

              {/* Table Content */}
              <div className="overflow-x-auto">
                <table className="w-full" style={{ tableLayout: 'auto' }}>
                  {positionsViewMode === 'positions' ? (
                    <>
                      <thead className="bg-white/5">
                        <tr className="text-white/80 text-xs font-semibold">
                          <th className="py-1.5 px-3 text-left">Type</th>
                          <th className="py-1.5 px-3 text-left">Instrument</th>
                          <th className="py-1.5 px-3 text-center">Qty</th>
                          <th className="py-1.5 px-3 text-center">BAvg</th>
                          <th className="py-1.5 px-3 text-center">SAvg</th>
                          <th className="py-1.5 px-3 text-center">LTP</th>
                          <th className="py-1.5 px-3 text-center">Day P&L</th>
                        </tr>
                      </thead>
                      <tbody className="text-xs">
                        {portfolioPositions.length > 0 ? (
                          portfolioPositions.map((pos, idx) => {
                            const posRowId = `pos-${idx}`;
                            const isHovered = hoveredPosition === posRowId;
                            return (
                              <tr key={idx}
                                className="border-b border-white/10 hover:bg-white/10 transition-colors text-white/90"
                                onMouseEnter={() => setHoveredPosition(posRowId)}
                                onMouseLeave={() => setHoveredPosition(null)}
                              >
                                <td className="py-1 px-3 text-left">{pos.type}</td>
                                <td className="py-1 px-3 text-left relative">
                                  <span>{pos.instrument}</span>
                                  {isHovered && Math.abs(parseInt(pos.qty)) > 0 && (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleExitPosition(pos); }}
                                      className="absolute right-1 top-1/2 -translate-y-1/2 bg-red-600 hover:bg-red-700 text-white text-[8px] font-bold rounded px-1.5 py-0.5 shadow-lg z-10"
                                      title={`Exit: ${Math.abs(parseInt(pos.qty))} qty`}
                                    >EX</button>
                                  )}
                                </td>
                                <td className={`py-1 px-3 text-center font-semibold ${pos.qty >= 0 ? 'text-green-400' : 'text-red-400'}`}>{pos.qty}</td>
                                <td className="py-1 px-3 text-center">{pos.buyAvg}</td>
                                <td className="py-1 px-3 text-center">{pos.sellAvg}</td>
                                <td className="py-1 px-3 text-center font-bold">{pos.ltp}</td>
                                <td className={`py-1 px-3 text-center font-bold ${parseFloat(pos.dayPnl) >= 0 ? 'text-green-400' : 'text-red-400'}`}>{pos.dayPnl}</td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr><td colSpan="7" className="p-6 text-center text-white/40">No positions found</td></tr>
                        )}
                      </tbody>
                    </>
                  ) : positionsViewMode === 'trades' ? (
                    (() => {
                      const sortedTrades = tradeBook && tradeBook.length > 0 ? [...tradeBook].sort((a, b) => {
                        if (!sortConfig.key) return 0;
                        let aVal, bVal;
                        const k = sortConfig.key;
                        if (k === 'instrument') { aVal = a.formattedInstrumentName || a.tradingSymbol || ''; bVal = b.formattedInstrumentName || b.tradingSymbol || ''; }
                        else if (k === 'type') { aVal = (a.formattedInstrumentName || '').match(/ (CE|PE)$/) ? (a.formattedInstrumentName || '').match(/ (CE|PE)$/)[1] : ''; bVal = (b.formattedInstrumentName || '').match(/ (CE|PE)$/) ? (b.formattedInstrumentName || '').match(/ (CE|PE)$/)[1] : ''; }
                        else if (k === 'bs') { aVal = a.transactionType || ''; bVal = b.transactionType || ''; }
                        else if (k === 'qty') { aVal = parseFloat(a.filledQuantity) || 0; bVal = parseFloat(b.filledQuantity) || 0; }
                        else if (k === 'price') { aVal = parseFloat(a.tradedPrice) || 0; bVal = parseFloat(b.tradedPrice) || 0; }
                        else if (k === 'time') { aVal = a.fillTimestamp || ''; bVal = b.fillTimestamp || ''; }
                        else return 0;
                        if (aVal < bVal) return sortConfig.dir === 'asc' ? -1 : 1;
                        if (aVal > bVal) return sortConfig.dir === 'asc' ? 1 : -1;
                        return 0;
                      }) : [];
                      const SortTh = ({ k, children, className = '' }) => (
                        <th className={`py-1 px-1.5 cursor-pointer select-none hover:text-white ${className}`}
                          onClick={() => setSortConfig(prev => ({ key: k, dir: prev.key === k && prev.dir === 'asc' ? 'desc' : 'asc' }))}>
                          {children}{sortConfig.key === k ? (sortConfig.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                        </th>
                      );
                      return (
                    <>
                      <thead className="bg-white/5">
                        <tr className="text-white/80 text-xs font-semibold">
                          <SortTh k="instrument" className="text-left">Instrument</SortTh>
                          <SortTh k="type" className="text-center">CE/PE</SortTh>
                          <SortTh k="bs" className="text-center">B/S</SortTh>
                          <SortTh k="qty" className="text-center">Qty</SortTh>
                          <SortTh k="price" className="text-right">Price</SortTh>
                          <SortTh k="time" className="text-right">Time</SortTh>
                        </tr>
                      </thead>
                      <tbody className="text-[13px]">
                        {sortedTrades.length > 0 ? (
                          sortedTrades.map((trade, idx) => {
                            const instName = trade.formattedInstrumentName || trade.tradingSymbol || '-';
                            const typeMatch = instName.match(/ (CE|PE)$/);
                            const optType = typeMatch ? typeMatch[1] : '';
                            const displayName = instName.replace(/ CE$| PE$/, '');
                            return (
                            <tr key={idx} className="border-b border-white/10 hover:bg-white/10 transition-colors text-white/90">
                              <td className="py-0.5 px-1.5 text-left font-semibold whitespace-nowrap">{displayName}</td>
                              <td className="py-0.5 px-1.5 text-center">
                                {optType ? (
                                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${optType === 'CE' ? 'bg-green-500/30 text-green-300' : 'bg-red-500/30 text-red-300'}`}>{optType}</span>
                                ) : '-'}
                              </td>
                              <td className={`py-0.5 px-1.5 text-center font-semibold ${trade.transactionType === 'BUY' ? 'text-green-400' : 'text-red-400'}`}>
                                {trade.transactionType || '-'}
                              </td>
                              <td className="py-0.5 px-1.5 text-center">{trade.filledQuantity || '-'}</td>
                              <td className="py-0.5 px-1.5 text-right font-medium">₹{trade.tradedPrice || '0.00'}</td>
                              <td className="py-0.5 px-1.5 text-right text-white/50 whitespace-nowrap">{trade.fillTimestamp || '-'}</td>
                            </tr>
                            );
                          })
                        ) : (
                          <tr><td colSpan="6" className="p-6 text-center text-white/40">No trades executed today</td></tr>
                        )}
                      </tbody>
                    </>
                      );
                    })()
                  ) : (
                    (() => {
                      const sortedOrders = orderBook && orderBook.length > 0 ? [...orderBook].sort((a, b) => {
                        if (!sortConfig.key) return 0;
                        let aVal, bVal;
                        const k = sortConfig.key;
                        if (k === 'instrument') { aVal = a.formattedInstrumentName || a.tradingSymbol || ''; bVal = b.formattedInstrumentName || b.tradingSymbol || ''; }
                        else if (k === 'type') { aVal = (a.formattedInstrumentName || '').match(/ (CE|PE)$/) ? (a.formattedInstrumentName || '').match(/ (CE|PE)$/)[1] : ''; bVal = (b.formattedInstrumentName || '').match(/ (CE|PE)$/) ? (b.formattedInstrumentName || '').match(/ (CE|PE)$/)[1] : ''; }
                        else if (k === 'bs') { aVal = a.transactionType || ''; bVal = b.transactionType || ''; }
                        else if (k === 'qty') { aVal = parseFloat(a.quantity) || 0; bVal = parseFloat(b.quantity) || 0; }
                        else if (k === 'price') { aVal = parseFloat(a.price) || 0; bVal = parseFloat(b.price) || 0; }
                        else if (k === 'status') { aVal = a.orderStatus || ''; bVal = b.orderStatus || ''; }
                        else if (k === 'orderid') { aVal = a.brokerOrderId || ''; bVal = b.brokerOrderId || ''; }
                        else if (k === 'time') { aVal = a.orderTime || ''; bVal = b.orderTime || ''; }
                        else return 0;
                        if (aVal < bVal) return sortConfig.dir === 'asc' ? -1 : 1;
                        if (aVal > bVal) return sortConfig.dir === 'asc' ? 1 : -1;
                        return 0;
                      }) : [];
                      const SortTh = ({ k, children, className = '' }) => (
                        <th className={`py-1 px-1.5 cursor-pointer select-none hover:text-white ${className}`}
                          onClick={() => setSortConfig(prev => ({ key: k, dir: prev.key === k && prev.dir === 'asc' ? 'desc' : 'asc' }))}>
                          {children}{sortConfig.key === k ? (sortConfig.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                        </th>
                      );
                      return (
                    <>
                      <thead className="bg-white/5">
                        <tr className="text-white/80 text-xs font-semibold">
                          <SortTh k="instrument" className="text-left">Instrument</SortTh>
                          <SortTh k="type" className="text-center">CE/PE</SortTh>
                          <SortTh k="bs" className="text-center">B/S</SortTh>
                          <SortTh k="qty" className="text-center">Qty</SortTh>
                          <SortTh k="price" className="text-right">Price</SortTh>
                          <SortTh k="status" className="text-center">Status</SortTh>
                          <SortTh k="orderid" className="text-left">Order ID</SortTh>
                          <SortTh k="time" className="text-right">Time</SortTh>
                        </tr>
                      </thead>
                      <tbody className="text-[13px]">
                        {sortedOrders.length > 0 ? (
                          sortedOrders.map((order, idx) => {
                            const orderRowId = `order-${idx}`;
                            const isHovered = hoveredOrder === orderRowId;
                            const status = (order.orderStatus || '').toUpperCase();
                            const isCancelable = ['OPEN', 'PENDING', 'TRIGGER PENDING', 'ORDERED'].includes(status);
                            const instName = order.formattedInstrumentName || order.tradingSymbol || '-';
                            const typeMatch = instName.match(/ (CE|PE)$/);
                            const optType = typeMatch ? typeMatch[1] : '';
                            const displayName = instName.replace(/ CE$| PE$/, '');
                            return (
                              <tr key={idx}
                                className="border-b border-white/10 hover:bg-white/10 transition-colors text-white/90"
                                onMouseEnter={() => setHoveredOrder(orderRowId)}
                                onMouseLeave={() => setHoveredOrder(null)}
                              >
                                <td className="py-0.5 px-1.5 text-left font-semibold whitespace-nowrap relative">
                                  {displayName}
                                  {isHovered && isCancelable && (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleCancelOrderFromBook(order); }}
                                      className="absolute right-0 top-1/2 -translate-y-1/2 bg-red-600 hover:bg-red-700 text-white text-[8px] font-bold rounded px-1.5 py-0.5 shadow-lg z-10"
                                      title={`Cancel: ${instName}`}
                                    >C</button>
                                  )}
                                </td>
                                <td className="py-0.5 px-1.5 text-center">
                                  {optType ? (
                                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${optType === 'CE' ? 'bg-green-500/30 text-green-300' : 'bg-red-500/30 text-red-300'}`}>{optType}</span>
                                  ) : '-'}
                                </td>
                                <td className={`py-0.5 px-1.5 text-center font-semibold ${order.transactionType === 'BUY' ? 'text-green-400' : 'text-red-400'}`}>
                                  {order.transactionType}
                                </td>
                                <td className="py-0.5 px-1.5 text-center">{order.quantity}</td>
                                <td className="py-0.5 px-1.5 text-right font-medium">{order.price}</td>
                                <td className="py-0.5 px-1.5 text-center">
                                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                                    status === 'COMPLETE' ? 'bg-green-500/30 text-green-300' :
                                    status === 'REJECTED' ? 'bg-red-500/30 text-red-300' :
                                    status === 'CANCELLED' ? 'bg-gray-500/30 text-gray-300' :
                                    'bg-yellow-500/30 text-yellow-300'
                                  }`}>
                                    {order.orderStatus}
                                  </span>
                                </td>
                                <td className="py-0.5 px-1.5 text-left text-white/50 whitespace-nowrap">{order.brokerOrderId || '-'}</td>
                                <td className="py-0.5 px-1.5 text-right text-white/50 whitespace-nowrap">{order.orderTime || '-'}</td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr><td colSpan="8" className="p-6 text-center text-white/40">No orders found</td></tr>
                        )}
                      </tbody>
                    </>
                      );
                    })()
                  )}
                </table>
              </div>
            </div>
          </div>
          )}
        </div>
      </div>

      {/* Position Selection Modal */}
      {showPositionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[20000]" onClick={() => setShowPositionModal(false)}>
          <div className="bg-gradient-to-br from-blue-900 to-purple-900 rounded-lg p-6 border-2 border-white/30 shadow-2xl max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-xl font-bold text-white">
                Add {selectedSymbolForPosition} to Position
              </h3>
              <div className="flex items-center gap-1.5 ml-3 flex-shrink-0">
                <span className="text-white/60 text-xs">Grid:</span>
                <select
                  value={gridRows}
                  onChange={(e) => {
                    const val = parseInt(e.target.value);
                    setGridRows(val);
                    localStorage.setItem('nseOptions_gridRows', val.toString());
                  }}
                  className="bg-white/20 text-white border border-white/30 rounded px-1.5 py-0.5 text-xs font-bold focus:outline-none focus:ring-1 focus:ring-blue-400"
                >
                  {Array.from({ length: 15 }, (_, i) => i + 1).map(r => (
                    <option key={r} value={r} className="bg-gray-800 text-white">
                      {r}({r * 4})
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <p className="text-white/70 mb-2 text-sm">
              Select which position to add this symbol:
            </p>
            <div className="flex items-center gap-4 mb-3 text-xs">
              <span className="flex items-center gap-1">
                <span className="inline-block w-3 h-3 rounded" style={{ backgroundColor: 'rgba(239,68,68,0.25)', border: '1px solid rgba(239,68,68,0.5)' }}></span>
                <span className="text-red-300">Occupied</span>
              </span>
              <span className="flex items-center gap-1">
                <span className="inline-block w-3 h-3 rounded" style={{ backgroundColor: 'rgba(34,197,94,0.2)', border: '1px solid rgba(34,197,94,0.5)' }}></span>
                <span className="text-green-300">Available</span>
              </span>
            </div>
            <div className="grid grid-cols-4 gap-2 mb-4 max-h-60 overflow-y-auto">
              {positionIds.map(pos => {
                const isOccupied = !!positions[pos];
                const isLocked = lockedPositions.has(pos);
                const occupiedSymbol = positions[pos];
                return (
                  <button
                    key={pos}
                    onClick={() => {
                      if (isLocked) return;
                      handlePositionSelect(pos);
                    }}
                    disabled={isLocked}
                    title={isOccupied ? `${occupiedSymbol}${isLocked ? ' (Locked)' : ''}` : 'Empty'}
                    className={`relative font-bold py-2 px-3 rounded-lg border transition-all text-sm ${
                      isLocked
                        ? 'bg-gray-700/50 border-gray-600 text-gray-500 cursor-not-allowed opacity-50'
                        : isOccupied
                          ? 'bg-red-500/20 hover:bg-red-500/35 text-red-200 border-red-500/40 hover:border-red-400'
                          : 'bg-green-500/20 hover:bg-green-500/35 text-green-200 border-green-500/40 hover:border-green-400 hover:scale-105'
                    }`}
                  >
                    <span>{pos}</span>
                    {isOccupied && (
                      <span className="block text-[9px] text-red-300/80 truncate leading-tight mt-0.5">
                        {occupiedSymbol}
                      </span>
                    )}
                    {isLocked && (
                      <span className="absolute -top-1 -right-1 text-[10px]">🔒</span>
                    )}
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => {
                setShowPositionModal(false);
                setSelectedSymbolForPosition(null);
              }}
              className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-lg transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Group Order Selection Modal */}
      {showGroupSelectModal && isMasterUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[20000]">
          <div className="bg-gradient-to-br from-blue-900 to-purple-900 rounded-lg shadow-xl p-6 w-full max-w-md mx-4 border-2 border-white/30">
            <h2 className="text-xl font-bold text-white mb-4">Select Trading Group</h2>
            <p className="text-white/60 mb-4 text-sm">Place order for your account only or for an entire group</p>
            
            <div className="space-y-3 max-h-96 overflow-y-auto mb-6">
              <div
                onClick={async () => {
                  if (!pendingOrderData) return;
                  setShowGroupSelectModal(false);
                  localStorage.removeItem('nseOptions_selectedGroup');
                  setSelectedGroupId(null);
                  try {
                    const response = await api.post('/orders/place', pendingOrderData);
                    if (response.data.success) {
                      setOrderMessage({ show: true, text: 'Order placed successfully!', type: 'success' });
                    } else {
                      setOrderMessage({ show: true, text: 'Order failed: ' + (response.data.error || 'Unknown'), type: 'error' });
                    }
                  } catch (error) {
                    setOrderMessage({ show: true, text: 'Failed: ' + (error.response?.data?.error || error.message), type: 'error' });
                  }
                  setTimeout(() => setOrderMessage({ show: false, text: '', type: '' }), 3000);
                  setPendingOrderData(null);
                }}
                className="p-4 rounded-lg border-2 cursor-pointer transition-all border-white/30 hover:border-blue-400 hover:bg-white/10"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-white">My Account Only</h3>
                    <p className="text-sm text-white/60">Trade for yourself</p>
                  </div>
                  <div className="text-2xl">👤</div>
                </div>
              </div>

              {userGroups.filter(g => g.is_active).map(group => (
                <div
                  key={group.id}
                  onClick={async () => {
                    if (!pendingOrderData) return;
                    setShowGroupSelectModal(false);
                    localStorage.setItem('nseOptions_selectedGroup', group.id.toString());
                    setSelectedGroupId(group.id.toString());
                    try {
                      const response = await api.post('/orders/place-group', {
                        groupId: parseInt(group.id),
                        ...pendingOrderData
                      });
                      if (response.data.success) {
                        let msg = `Group order placed! Success: ${response.data.successCount}, Failed: ${response.data.failCount}`;
                        if (response.data.needsReloginCount > 0) {
                          msg += ` (${response.data.needsReloginCount} need re-login)`;
                        }
                        setOrderMessage({ show: true, text: msg, type: 'success' });
                      } else {
                        setOrderMessage({ show: true, text: 'Group order failed: ' + (response.data.error || 'Unknown'), type: 'error' });
                      }
                    } catch (error) {
                      setOrderMessage({ show: true, text: 'Failed: ' + (error.response?.data?.error || error.message), type: 'error' });
                    }
                    setTimeout(() => setOrderMessage({ show: false, text: '', type: '' }), 4000);
                    setPendingOrderData(null);
                  }}
                  className="p-4 rounded-lg border-2 cursor-pointer transition-all border-white/30 hover:border-green-400 hover:bg-white/10"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-white">{group.group_name}</h3>
                      <p className="text-sm text-white/60">
                        {group.member_count} member{group.member_count !== 1 ? 's' : ''} | Default Lots: {group.default_lots}
                      </p>
                    </div>
                    <div className="text-2xl">👥</div>
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={() => {
                setShowGroupSelectModal(false);
                setPendingOrderData(null);
              }}
              className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-lg transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Global Order Toast */}
      {orderMessage.show && (
        <div className="fixed top-4 right-4 z-[30000] animate-fadeIn">
          <div className={`px-6 py-4 rounded-lg shadow-2xl border-2 ${
            orderMessage.type === 'success' 
              ? 'bg-green-900 border-green-500 text-green-200' 
              : 'bg-red-900 border-red-500 text-red-200'
          }`}>
            <div className="flex items-center gap-3">
              <span className="text-2xl">{orderMessage.type === 'success' ? '✅' : '❌'}</span>
              <span className="font-semibold text-base">{orderMessage.text}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NseOptionsTrade;
